import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  HiOutlineDocumentReport,
  HiOutlineDownload,
  HiOutlineFilter,
  HiOutlineX,
  HiOutlineAcademicCap,
  HiOutlineSearch,
  HiOutlineEye,
  HiOutlineEyeOff,
} from "react-icons/hi";
import Pagination from "../components/Pagination";
import {
  generateStudentLevelReport,
  exportStudentLevelReportExcel,
} from "../services/reportingApi";
import { fetchMasterData, fetchAssessmentYears, fetchSchools, fetchCycles } from "../services/masterDataApi";
import { useUserRole } from "../hooks/useUserRole";
import { fetchMyStates, fetchMyDistricts, fetchMyBlocks } from "../services/assignmentApi";
import SearchableDropdown from "../components/SearchableDropdown";

function StudentLevelReport() {
  const { role, userProfile } = useUserRole();
  const [states, setStates] = useState([]);
  const [plDistricts, setPlDistricts] = useState([]);
  const [pmBlocks, setPmBlocks] = useState([]);
  const [assessmentYears, setAssessmentYears] = useState([]);
  const [cycles, setCycles] = useState([]);

  const [filters, setFilters] = useState({
    state_id: "",
    assessment_year_id: "",
    cycle_id: "",
    district_id: "",
    block_id: "",
    school_id: "",
  });


  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [collapsedRounds, setCollapsedRounds] = useState(new Set());
  const searchRef = useRef(null);

  const toggleRound = (round) => {
    setCollapsedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(round)) next.delete(round);
      else next.add(round);
      return next;
    });
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load states based on role
  useEffect(() => {
    if (!role) return;
    // ADMIN's State picker is a SearchableDropdown that fetches its own options.
    if (role === "SPL") {
      fetchMyStates().then((myStates) => {
        if (myStates && myStates.length > 0) {
          setStates(myStates);
          setFilters((prev) => ({ ...prev, state_id: String(myStates[0].id) }));
        }
      }).catch(() => {});
    } else if (role === "PL") {
      fetchMyDistricts().then((myDistricts) => {
        if (myDistricts && myDistricts.length > 0) {
          setPlDistricts(myDistricts);
          // A PL's districts can span more than one state — dedupe to every
          // state actually represented, not just the first district's, so
          // the State dropdown below can offer all of them.
          const uniqueStates = [...new Map(
            myDistricts.map((d) => [d.state_id, { id: d.state_id, name: d.state_name }])
          ).values()];
          setStates(uniqueStates);
          setFilters((prev) => ({ ...prev, state_id: String(myDistricts[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PM") {
      // PM has no state of their own — it's wherever their assigned blocks
      // sit, so derive it from /pm/my-blocks the same way School Wise
      // Report does, instead of a userProfile.state_id that the profile
      // endpoint never actually returns (see states-loading below too).
      fetchMyBlocks().then((myBlocks) => {
        if (myBlocks && myBlocks.length > 0) {
          setPmBlocks(myBlocks);
          setFilters((prev) => ({ ...prev, state_id: String(myBlocks[0].state_id) }));
        }
      }).catch(() => {});
    } else if (role === "PO" && userProfile?.state_id) {
      setFilters((prev) => ({ ...prev, state_id: String(userProfile.state_id) }));
    }
  }, [role, userProfile]);

  // Assessment years don't depend on the state — fetch once, show once a
  // state is picked.
  const allYearsRef = useRef(null);
  useEffect(() => {
    if (filters.state_id) {
      if (!allYearsRef.current) {
        allYearsRef.current = fetchAssessmentYears()
          .then((res) => (res.success ? res.data || [] : []))
          .catch(() => { allYearsRef.current = null; return []; });
      }
      allYearsRef.current.then(setAssessmentYears);
    } else {
      setAssessmentYears([]);
      setCycles([]);
    }
  }, [filters.state_id]);

  // Load cycles when state + assessment year changes
  useEffect(() => {
    if (filters.state_id && filters.assessment_year_id) {
      const fetchCyclesList = async () => {
        try {
          // Light cycles list instead of running a whole School Wise report
          // just to read its available_cycles. Same rows, same order
          // (continuous cycles of this state + year, by round number).
          const res = await fetchCycles({
            state_id: Number(filters.state_id),
            cycle_type: "continuous",
            assessment_year_id: filters.assessment_year_id,
            page: 1,
            page_size: 1000,
          });
          setCycles(
            (res.data || [])
              .map((c) => ({ id: c.id, title: c.title, round_number: c.round_number }))
              .sort((a, b) => (a.round_number ?? 0) - (b.round_number ?? 0))
          );
        } catch {
          setCycles([]);
        }
      };
      fetchCyclesList();
    } else {
      setCycles([]);
    }
  }, [filters.state_id, filters.assessment_year_id]);


  // Generate report
  const loadReport = useCallback(async () => {
    if (!filters.state_id || !filters.school_id) {
      setReportData(null);
      return;
    }
    setLoading(true);
    try {
      const payload = { state_id: Number(filters.state_id), school_id: Number(filters.school_id), page, page_size: pageSize };
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;
      if (debouncedSearch.trim()) payload.search = debouncedSearch.trim();

      const res = await generateStudentLevelReport(payload);
      if (res.success) {
        setReportData(res.data);
      }
    } catch (err) {
      console.error("Report generation failed:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, debouncedSearch]);

  useEffect(() => {
    if (filters.state_id && filters.school_id) {
      loadReport();
    }
  }, [loadReport]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "state_id") {
      next.assessment_year_id = "";
      next.cycle_id = "";
      next.district_id = "";
      next.block_id = "";
      next.school_id = "";
    }
    if (key === "assessment_year_id") {
      next.cycle_id = "";
    }
    if (key === "district_id") {
      next.block_id = "";
      next.school_id = "";
    }
    if (key === "block_id") {
      next.school_id = "";
    }
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    const base = { state_id: "", assessment_year_id: "", cycle_id: "", district_id: "", block_id: "", school_id: "" };
    if (role === "SPL" || role === "PL" || role === "PM" || role === "PO") {
      base.state_id = filters.state_id;
    }
    setFilters(base);
    setPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
    setReportData(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {};
      if (filters.state_id) payload.state_id = Number(filters.state_id);
      if (filters.school_id) payload.school_id = Number(filters.school_id);
      if (filters.district_id) payload.district_id = Number(filters.district_id);
      if (filters.block_id) payload.block_id = Number(filters.block_id);
      if (filters.assessment_year_id) payload.assessment_year_id = filters.assessment_year_id;
      if (filters.cycle_id) payload.cycle_id = filters.cycle_id;

      const blob = await exportStudentLevelReportExcel(payload);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "student_level_report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = Object.values(filters).some((v) => v !== "");
  const students = reportData?.students || [];
  const competencies = reportData?.competencies || [];
  const categories = reportData?.categories || [];
  const pagination = reportData?.pagination || {};
  const schoolInfo = reportData?.school_info;
  const visitInfo = reportData?.visit_info || [];

  // competencies is already sorted by origin_round, so consecutive same-round
  // entries form contiguous column groups — one pass is enough to bucket them.
  const roundGroups = [];
  for (const comp of competencies) {
    const last = roundGroups[roundGroups.length - 1];
    if (last && last.round === comp.origin_round) last.count += 1;
    else roundGroups.push({ round: comp.origin_round, count: 1 });
  }
  // One entry per physical column under the group header row: a real
  // competency for a visible round, or a single collapsed placeholder that
  // swallows a whole hidden round's columns — keeps every row's cell count
  // lined up with the group header's colSpan even while rounds are hidden.
  const columnPlan = [];
  {
    let idx = 0;
    for (const g of roundGroups) {
      if (collapsedRounds.has(g.round)) {
        columnPlan.push({ collapsed: true, key: `hidden-${g.round}` });
        idx += g.count;
      } else {
        for (let k = 0; k < g.count; k++) {
          columnPlan.push({ collapsed: false, comp: competencies[idx] });
          idx += 1;
        }
      }
    }
  }

  const selectCls =
    "h-[34px] border border-[#d0d5dd] rounded-lg px-2.5 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all appearance-none w-full";

  const statusIcon = (status, achievedRound) => {
    if (status === "pass") return <span title="Mastered" className="text-green-600 font-bold text-[15px] cursor-default">✓</span>;
    if (status === "fail") return <span title="Not Mastered" className="text-red-500 font-bold text-[15px] cursor-default">✗</span>;
    if (status === "absent") return <span title="Absent" className="text-yellow-600 italic text-[11px] cursor-default">Absent</span>;
    if (status === "not_asked") {
      const title = achievedRound ? `Already passed in Round ${achievedRound} — not re-asked this round` : "Already passed — not re-asked this round";
      return <span title={title} className="text-[#8b949e] italic text-[10px] cursor-default">Not Asked</span>;
    }
    return <span title="Competency Not Tested" className="text-[#8b949e] italic text-[10px] cursor-default">Not Tested</span>;
  };

  const ROUND_PASTELS = ["#dbeafe", "#dcfce7", "#fef3c7", "#fce7f3", "#ede9fe", "#e0f2fe"];

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[18px] font-bold text-[#24292f] flex items-center gap-2">
            <HiOutlineDocumentReport className="text-[#0078d4]" />
            Student Level Report
          </h1>
          <p className="text-[12px] text-[#57606a] mt-0.5">
            Student-wise competency report for a selected school
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || students.length === 0}
          className="inline-flex items-center gap-2 h-[36px] px-4 text-[12.5px] font-medium rounded-lg cursor-pointer bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HiOutlineDownload className="text-sm" />
          {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] mb-5">
        <div className="px-5 py-3 border-b border-[#f0f0f0] flex items-center gap-2">
          <HiOutlineFilter className="text-[#8b949e]" />
          <span className="text-[11px] font-semibold text-[#8b949e] uppercase tracking-wider">
            Filters
          </span>
        </div>
        <div className="px-5 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">State</label>
              {role === "PM" ? (
                <select value={filters.state_id} onChange={(e) => handleFilterChange("state_id", e.target.value)} className={selectCls}>
                  {[...new Map(pmBlocks.map(b => [b.state_id, { id: b.state_id, name: b.state_name }])).values()]
                    .map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              ) : (role === "SPL" || role === "PL") ? (
                <select value={filters.state_id} onChange={(e) => handleFilterChange("state_id", e.target.value)} className={selectCls}>
                  {states.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              ) : role === "PO" ? (
                <div className={selectCls + " bg-[#f6f8fa] text-[#57606a] flex items-center"}>
                  {states.find(s => String(s.id) === filters.state_id)?.name || (filters.state_id ? `State #${filters.state_id}` : "—")}
                </div>
              ) : (
                <SearchableDropdown
                  value={String(filters.state_id || "")}
                  onChange={(id) => handleFilterChange("state_id", id)}
                  placeholder="Select State"
                  fetchFn={(search, page = 1) => fetchMasterData("states", {
                    page, page_size: 20, search,
                    has_assessment: true,
                    cycle_type: "continuous",
                  })}
                  variant="report"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Assessment Year</label>
              <select value={filters.assessment_year_id} onChange={(e) => handleFilterChange("assessment_year_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">Select Assessment Year</option>
                {assessmentYears.map((ay) => (<option key={ay.id} value={ay.id}>{ay.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Cycle / Round</label>
              <select value={filters.cycle_id} onChange={(e) => handleFilterChange("cycle_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                <option value="">Select Cycle</option>
                {cycles.map((c) => (<option key={c.id} value={c.id}>{c.title || `Round ${c.round_number}`}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">District</label>
              {role === "PL" ? (
                <select value={filters.district_id} onChange={(e) => handleFilterChange("district_id", e.target.value)} className={selectCls} disabled={!filters.state_id}>
                  <option value="">Select District</option>
                  {plDistricts.slice().sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <SearchableDropdown
                  key={`slr-dist-${filters.state_id}-${filters.cycle_id || "all"}`}
                  value={filters.district_id}
                  onChange={(id) => handleFilterChange("district_id", id)}
                  placeholder="Select District"
                  fetchFn={(search, page = 1) => fetchMasterData("districts", {
                    page, page_size: 20,
                    state_id: Number(filters.state_id),
                    search,
                    has_assessment: true,
                    cycle_id: filters.cycle_id || undefined,
                    cycle_type: "continuous",
                  })}
                  disabled={!filters.state_id}
                  variant="report"
                  emptyMessage="No districts with completed assessments"
                />
              )}
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">Block</label>
              <SearchableDropdown
                key={`slr-blk-${filters.district_id}-${filters.cycle_id || "all"}`}
                value={filters.block_id}
                onChange={(id) => handleFilterChange("block_id", id)}
                placeholder="Select Block"
                fetchFn={(search, page = 1) => fetchMasterData("blocks", {
                  page, page_size: 20,
                  ...(filters.district_id ? { district_id: Number(filters.district_id) } : { state_id: Number(filters.state_id) }),
                  search,
                  has_assessment: true,
                  cycle_id: filters.cycle_id || undefined,
                  cycle_type: "continuous",
                })}
                disabled={!filters.district_id}
                variant="report"
                emptyMessage="No blocks with completed assessments"
              />
            </div>
            <div>
              <label className="block text-[10.5px] font-medium text-[#8b949e] mb-1 uppercase tracking-wide">School</label>
              <SearchableDropdown
                key={`slr-sch-${filters.block_id}-${filters.cycle_id || "all"}`}
                value={filters.school_id}
                onChange={(id) => handleFilterChange("school_id", id)}
                placeholder="Select School"
                fetchFn={(search, page = 1) => fetchSchools({
                  page, page_size: 20,
                  block_id: Number(filters.block_id),
                  is_adopted: true,
                  has_assessment: true,
                  cycle_id: filters.cycle_id || undefined,
                  cycle_type: "continuous",
                  search,
                })}
                disabled={!filters.block_id}
                variant="report"
                emptyMessage="No schools with completed assessments"
              />
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3">
              <button onClick={clearFilters} className="inline-flex items-center gap-1 h-[30px] px-3 text-[11px] font-medium rounded-lg cursor-pointer bg-[#f3f4f6] text-[#57606a] border border-[#d0d5dd] hover:bg-[#e5e7eb] transition-all">
                <HiOutlineX className="text-xs" /> Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Table */}
      {!filters.school_id ? (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-10 text-center">
          <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
          <p className="text-[13px] text-[#57606a]">Select a school to generate the student-level report.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          {/* School info header — stays mounted across loading/search refreshes so the search input never loses focus */}
          <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#f8f9fa]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-[13px] font-bold text-[#24292f] uppercase tracking-wider">
                  {schoolInfo ? `${schoolInfo.name}` : "Student Level Report"} ({pagination.total_count || 0} students)
                </h3>
              </div>
              <div className="relative flex-1 max-w-md">
                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e] text-sm" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search by student name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-[34px] w-full border border-[#d0d5dd] rounded-lg pl-9 pr-3 text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-[#e5e7eb] rounded w-48 mx-auto" />
                <div className="h-40 bg-[#e5e7eb] rounded" />
              </div>
            </div>
          ) : students.length === 0 ? (
            <div className="p-10 text-center">
              <HiOutlineAcademicCap className="mx-auto text-3xl text-[#d0d5dd] mb-2" />
              <p className="text-[13px] text-[#57606a]">No students found. Try adjusting your filters or search.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  {roundGroups.length > 1 && (
                    <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                      <th
                        rowSpan={2}
                        className="text-left px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide sticky left-0 bg-[#f6f8fa] z-10 align-bottom"
                      >
                        Student
                      </th>
                      {roundGroups.map((g, i) => {
                        const isCollapsed = collapsedRounds.has(g.round);
                        return (
                          <th
                            key={i}
                            colSpan={isCollapsed ? 1 : g.count}
                            className="text-center px-3 py-1.5 text-[10.5px] font-bold text-[#57606a] uppercase tracking-wide border-l border-[#e5e7eb]"
                            style={{ backgroundColor: ROUND_PASTELS[i % ROUND_PASTELS.length] }}
                          >
                            <button
                              type="button"
                              onClick={() => toggleRound(g.round)}
                              title={isCollapsed ? `Show Round ${g.round} competencies` : `Hide Round ${g.round} competencies`}
                              className="inline-flex items-center gap-1 bg-transparent border-none cursor-pointer text-[12px] font-bold text-[#57606a] uppercase tracking-wide p-0"
                            >
                              {isCollapsed ? <HiOutlineEyeOff className="text-[12px] shrink-0" /> : <HiOutlineEye className="text-[12px] shrink-0" />}
                              {g.round ? `Round ${g.round}` : "Round —"}
                            </button>
                          </th>
                        );
                      })}
                      <th rowSpan={2} className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide align-bottom">Score</th>
                      {categories.map((cat) => (
                        <th key={cat.name} rowSpan={2} className="text-center px-3 py-3 text-[11px] font-bold uppercase tracking-wide align-bottom" style={{ color: cat.color || "#24292f" }}>
                          Category {cat.name}
                        </th>
                      ))}
                    </tr>
                  )}
                  <tr className="bg-[#f6f8fa] border-b-2 border-[#d0d5dd]">
                    {roundGroups.length <= 1 && (
                      <th className="text-left px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide sticky left-0 bg-[#f6f8fa] z-10">Student</th>
                    )}
                    {columnPlan.map((col, i) => col.collapsed ? (
                      <th key={col.key} className="text-center px-3 py-3 text-[11px] text-[#c0c5cc]">⋯</th>
                    ) : (
                      <th key={col.comp.code} className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide whitespace-nowrap" title={col.comp.name}>
                        {col.comp.code || `COMP${String(i + 1).padStart(3, '0')}`}
                      </th>
                    ))}
                    {roundGroups.length <= 1 && (
                      <>
                        <th className="text-center px-3 py-3 text-[11px] font-bold text-[#24292f] uppercase tracking-wide">Score</th>
                        {categories.map((cat) => (
                          <th key={cat.name} className="text-center px-3 py-3 text-[11px] font-bold uppercase tracking-wide" style={{ color: cat.color || "#24292f" }}>
                            Category {cat.name}
                          </th>
                        ))}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e7eb]">
                  {students.map((student, idx) => (
                    <tr key={student.student_id || idx} className="hover:bg-[#f6f8fa] transition-colors">
                      <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-medium sticky left-0 bg-white z-10 whitespace-nowrap">{student.student_name}</td>
                      {columnPlan.map((col) => col.collapsed ? (
                        <td key={col.key} className="px-3 py-2 text-center text-[#c0c5cc]">⋯</td>
                      ) : (
                        <td key={col.comp.code} className="px-3 py-2 text-center">
                          {statusIcon(student.competency_results?.[col.comp.code], student.competency_achieved_round?.[col.comp.code])}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-[12.5px] text-[#24292f] font-semibold text-center">{student.score_numerator}/{student.score_denominator}</td>
                      {categories.map((cat) => (
                        <td key={cat.name} className="px-3 py-2 text-center">
                          {student.category === cat.name ? <span className="text-green-600 font-bold">✅</span> : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && students.length > 0 && (
            <div className="px-4 py-3 border-t border-[#e5e7eb] bg-[#f8f9fa]">
              <p className="text-[10.5px] text-[#57606a] mb-1 font-semibold">Symbols:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <p className="text-[10px] text-[#8b949e]">
                  <span className="text-green-600 font-bold mr-1">✓</span>
                  <strong>Mastered</strong> — student passed this competency
                </p>
                <p className="text-[10px] text-[#8b949e]">
                  <span className="text-red-500 font-bold mr-1">✗</span>
                  <strong>Not Mastered</strong> — student did not pass this competency
                </p>
                <p className="text-[10px] text-[#8b949e]">
                  <strong>Absent</strong> — student was absent for this competency's assessment
                </p>
                <p className="text-[10px] text-[#8b949e]">
                  <strong>Not Tested</strong> — this competency has not been assessed for the student yet
                </p>
                <p className="text-[10px] text-[#8b949e]">
                  <strong>Not Asked</strong> — student already passed this competency in an earlier round, so it wasn't asked again this round
                </p>
              </div>
            </div>
          )}

          {pagination.total_pages > 1 && students.length > 0 && (
            <Pagination currentPage={pagination.page} totalPages={pagination.total_pages} totalCount={pagination.total_count} pageSize={pagination.page_size} onPageChange={setPage} entityName="students" />
          )}
        </div>
      )}
    </div>
  );
}

export default StudentLevelReport;
